'use client';

import React from 'react';
import { motion } from 'framer-motion';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import { MotionLink } from '@/components/ui/Motion';
import { TypographySmall } from '@/components/ui/typography';
import { cls } from '@/lib/utils';
import type { UrlProjectName } from '../../../types';
import { PROJECT_DATA_MAP } from '../../data';

const SECTION_ID = 'related-projects';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  },
};

const cardMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  },
};

interface RelatedProjectsSectionProperties {
  relatedProjects: UrlProjectName[];
}

function RelatedProjectsSection({
  relatedProjects,
}: RelatedProjectsSectionProperties) {
  const projects = relatedProjects
    .map((slug) => PROJECT_DATA_MAP[slug])
    .filter(Boolean);

  if (projects.length === 0) return null;

  return (
    <SectionBase id={SECTION_ID} background="bg-background">
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperMotionProps}
      >
        <SectionHeader
          subtitle="MORE PROJECTS"
          title="Explore Related Projects"
          description="Discover more custom homes built by Cornerstone Homes in Claremore and the surrounding areas."
        />

        <div className={cls`grid grid-cols-1 sm:grid-cols-3 gap-6 w-full`}>
          {projects.map((project) => (
            <motion.div key={project.urlSlug} {...cardMotionProps}>
              <MotionLink
                href={`/projects/${project.urlSlug}`}
                className={cls`block rounded-lg overflow-hidden bg-card border border-border hover:shadow-lg transition-shadow`}
              >
                <div className="p-6 flex flex-col gap-3 text-center">
                  <span className="text-xs uppercase tracking-wider font-semibold text-primary">
                    {project.style}
                  </span>
                  <span className="text-xl font-prata text-foreground">
                    {project.name}
                  </span>

                  {project.sqft && (
                    <TypographySmall className="text-muted-foreground">
                      {project.sqft} Sq. Ft.
                    </TypographySmall>
                  )}

                  {/* Spec badges */}
                  <div className="flex flex-wrap justify-center gap-2">
                    {project.beds && (
                      <span className="bg-primary text-primary-foreground text-xs px-3 py-1 rounded-sm font-medium">
                        {project.beds} Beds
                      </span>
                    )}
                    {project.baths && (
                      <span className="bg-primary text-primary-foreground text-xs px-3 py-1 rounded-sm font-medium">
                        {project.baths} Baths
                      </span>
                    )}
                    {project.garage && (
                      <span className="bg-primary text-primary-foreground text-xs px-3 py-1 rounded-sm font-medium">
                        {project.garage}
                      </span>
                    )}
                  </div>
                </div>
              </MotionLink>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </SectionBase>
  );
}

export default RelatedProjectsSection;
